import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldLogo } from '../components/ShieldLogo'
import { LangThemePicker } from '../components/LangThemePicker'
import { DemoRequest } from '../components/DemoRequest'
import type { ClientsBand } from '../components/DemoRequest'
import { useLang } from '../contexts/LangContext'
import { PLANS } from '../config/plans'

// As capturas vêm do harness em demo/ensaio.mjs, que serve fixtures fictícias
// para os componentes reais do produto. Nunca de sessão logada em produção: a
// carteira é dado de terceiro sob custódia. Se um PNG sair de public/, virar
// esta constante de volta, porque moldura vazia lê como imagem quebrada.
const SHOTS_READY = true

// A página carrega a versão .webp (cwebp -q 88 do PNG ao lado, ~70% menor). O PNG
// continua em public/ porque é o que demo/capturar.mjs gera: recapturou, reconverte.
const SHOTS = [
  { file: '/landing-overview.webp',              chrome: 'offices.immigrantshield.com/overview', capKey: 'shot1Caption', altKey: 'shot1Alt' },
  { file: '/landing-cases-acao-necessaria.webp', chrome: '/cases',                                capKey: 'shot2Caption', altKey: 'shot2Alt' },
  { file: '/landing-alerts.webp',                chrome: '/alerts',                               capKey: 'shot3Caption', altKey: 'shot3Alt' },
]


// Escura fixa por decisão de marca, então os tokens não podem ser os globais:
// se fossem, a landing seguiria o tema escolhido pelo usuário no app. Estes
// valores são os mesmos do bloco .dark em src/index.css.
const LANDING_CSS = `
.lp {
  --lp-bg:#0D1814; --lp-card:#13211B; --lp-subtle:#182821; --lp-pill:#203129;
  --lp-line:#23362D; --lp-text:#F0F5F2; --lp-text-2:#A9B7AF; --lp-text-3:#7F8E87;
  --lp-brand:#22B884; --lp-brand-hover:#2AC490;
  --lp-brand-soft:rgba(34,184,132,.12); --lp-brand-edge:rgba(34,184,132,.28);
  --lp-deep:#0A1511;
  --lp-shadow:0 22px 48px -20px rgba(0,0,0,.75), 0 2px 8px rgba(0,0,0,.4);
  background:var(--lp-bg); color:var(--lp-text); overflow-x:hidden;
  font-size:16px; line-height:1.55;
}
.lp *, .lp *::before, .lp *::after { box-sizing:border-box; }
.lp-wrap { max-width:1152px; margin:0 auto; padding-inline:20px; }
.lp section[id] { scroll-margin-top:84px; }
.lp-band { padding-block:60px; }
.lp-canvas { background:var(--lp-bg); }
.lp-lift { background:var(--lp-card); border-block:1px solid var(--lp-line); }
.lp-deep { background:var(--lp-deep); border-block:1px solid var(--lp-brand-edge); }

.lp-eyebrow { font-size:12px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:var(--lp-brand); margin:0 0 14px; }
.lp h1 { font-size:clamp(32px,5.2vw,50px); font-weight:800; line-height:1.06; letter-spacing:-.025em; margin:0 0 18px; text-wrap:balance; }
.lp h2 { font-size:clamp(25px,3.6vw,36px); font-weight:800; line-height:1.13; letter-spacing:-.02em; margin:0; text-wrap:balance; }
.lp h3 { font-size:17px; font-weight:700; margin:0 0 6px; letter-spacing:-.01em; }
.lp p { margin:0 0 16px; color:var(--lp-text-2); }
.lp-lead { font-size:clamp(16px,1.9vw,19px); max-width:62ch; }

/* eyebrow em linha própria: se ficar dentro da coluna, a coluna da direita
   alinha o topo com ele em vez de com o título */
.lp-head { margin-bottom:32px; }
.lp-head-cols { display:grid; gap:14px 48px; align-items:start; }
.lp-head-cols > div > p:last-child { margin-bottom:0; }
@media (min-width:900px) {
  .lp-head-cols { grid-template-columns:1fr 1fr; }
  .lp-head-cols > div { padding-top:9px; }
}

.lp-grid { display:grid; gap:1px; background:var(--lp-line); border:1px solid var(--lp-line); border-radius:8px; overflow:hidden; }
@media (min-width:760px) { .lp-g3 { grid-template-columns:repeat(3,1fr); } }
.lp-cell { background:var(--lp-card); padding:24px 22px; transition:background-color .18s ease; }
.lp-lift .lp-cell { background:var(--lp-subtle); }
.lp-deep .lp-cell { background:#0F1C17; }
.lp-cell:hover { background:var(--lp-pill); }
.lp-lift .lp-cell:hover { background:var(--lp-card); }
.lp-deep .lp-cell:hover { background:#13211B; }
.lp-cell p { margin:0; font-size:14.5px; }

.lp-step { position:relative; overflow:hidden; }
.lp-step .lp-num { position:absolute; right:16px; top:14px; font-size:46px; font-weight:800; line-height:1; color:var(--lp-brand); opacity:.22; letter-spacing:-.04em; font-variant-numeric:tabular-nums; pointer-events:none; }
.lp-step h3 { padding-right:56px; }

.lp-mark { width:32px; height:32px; display:grid; place-items:center; border-radius:8px; background:var(--lp-brand-soft); color:var(--lp-brand); margin-bottom:12px; }
.lp-mark svg { width:17px; height:17px; }

.lp-btn { display:inline-flex; align-items:center; gap:8px; font:inherit; font-size:15px; font-weight:700; padding:13px 22px; border-radius:8px; border:1px solid transparent; text-decoration:none; cursor:pointer; transition:background-color .18s ease, border-color .18s ease; }
.lp-btn svg { transition:transform .18s ease; }
.lp-btn:hover svg { transform:translateX(3px); }
.lp-btn-primary { background:var(--lp-brand); color:#04120C; }
.lp-btn-primary:hover { background:var(--lp-brand-hover); }
.lp-btn-ghost { background:transparent; color:var(--lp-text); border-color:var(--lp-line); }
.lp-btn-ghost:hover { border-color:var(--lp-text-3); }
.lp-btn-row { display:flex; flex-wrap:wrap; gap:12px; margin-bottom:16px; }

.lp-scope { border-left:2px solid var(--lp-brand); padding:2px 0 2px 16px; font-size:13.5px; color:var(--lp-text-2); max-width:64ch; margin:0; }

.lp-chips { display:flex; flex-wrap:wrap; gap:8px; margin-top:18px; }
.lp-chip { display:inline-flex; align-items:center; gap:7px; font-size:12.5px; font-weight:700; padding:5px 12px 5px 10px; border-radius:999px; border:1px solid; }
.lp-chip::before { content:""; width:7px; height:7px; border-radius:50%; background:currentColor; }
.lp-chip-crit { color:#f87171; border-color:rgba(239,68,68,.45); background:rgba(239,68,68,.07); }
.lp-chip-warn { color:#fbbf24; border-color:rgba(245,158,11,.45); background:rgba(245,158,11,.07); }
.lp-chip-info { color:var(--lp-brand); border-color:var(--lp-brand-edge); background:var(--lp-brand-soft); }

/* hero de fora a fora: a imagem atravessa a tela, o texto fica na faixa escura
   da esquerda e começa na mesma margem do logo */
.lp-hero { position:relative; background:var(--lp-bg); border-bottom:1px solid var(--lp-line); }
.lp-hero-media { position:relative; width:100%; overflow:hidden; }
.lp-hero-media img { display:block; width:100%; height:auto; }
.lp-hero-media::after {
  content:""; position:absolute; inset:0; pointer-events:none;
  background:
    linear-gradient(to top, var(--lp-bg) 0%, rgba(13,24,20,0) 26%),
    linear-gradient(to right, var(--lp-bg) 0%, rgba(13,24,20,.96) 38%, rgba(13,24,20,.62) 60%, rgba(13,24,20,0) 82%);
}
.lp-hero-copy { padding-block:56px 44px; }
.lp-hero-copy .lp-col { max-width:600px; }
.lp-photo-cap { font-size:12.5px; color:var(--lp-text-3); margin:12px 0 0; }
@media (min-width:980px) {
  /* a foto e 21:9, entao a altura dela e a largura x 0,424, e a coluna de texto
     tem 512px: a sobreposicao so caberia acima de ~1210px. Sem piso de altura,
     entre 980 e 1210 o texto centrado transborda nas duas pontas - o eyebrow
     sobe para tras do header grudento e o disclaimer cai na secao seguinte.
     aspect-ratio mantem a proporcao natural nas telas largas, min-height segura
     a faixa estreita, e object-fit recorta pelos lados em vez de deformar. */
  .lp-hero-media { aspect-ratio:1584/672; min-height:584px; }
  .lp-hero-media img { position:absolute; inset:0; height:100%; object-fit:cover; object-position:70% center; }
  .lp-hero-copy { position:absolute; inset:0; z-index:1; display:flex; align-items:center; padding-block:0; }
  .lp-hero-copy > .lp-wrap { width:100%; }
  .lp-hero-copy .lp-col { max-width:560px; }
  .lp-photo-cap { position:absolute; right:20px; bottom:14px; margin:0; max-width:56ch; text-align:right; z-index:2; }
}
@media (max-width:979px) {
  .lp-hero-media img { aspect-ratio:16/11; object-fit:cover; object-position:78% center; }
  /* abaixo de 980 a legenda sai do absoluto e fica fora do .lp-wrap: sem isto encosta na borda */
  .lp-photo-cap { padding-inline:20px; }
}
@media (min-width:600px) and (max-width:979px) {
  /* 16:11 e para celular. Numa janela de notebook dividida (~950px) virava 650px
     de foto e empurrava titulo e botao para fora da primeira tela. Aqui a foto
     volta a proporcao nativa, a mesma do desktop, e fica como faixa. */
  .lp-hero-media img { aspect-ratio:1584/672; object-position:70% center; }
}

.lp-shot { margin:0; border:1px solid var(--lp-line); border-radius:8px; background:var(--lp-card); box-shadow:var(--lp-shadow); overflow:hidden; transition:border-color .18s ease, transform .18s ease; }
.lp-shot:hover { border-color:var(--lp-brand-edge); }
.lp-shot img { display:block; width:100%; height:auto; }
.lp-chrome { display:flex; align-items:center; gap:6px; padding:9px 13px; border-bottom:1px solid var(--lp-line); background:var(--lp-subtle); }
.lp-chrome i { width:8px; height:8px; border-radius:50%; background:var(--lp-line); flex:none; }
.lp-chrome span { margin-left:10px; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:10.5px; color:var(--lp-text-2); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.lp-shot figcaption { border-top:1px solid var(--lp-line); padding:11px 15px; font-size:12.5px; color:var(--lp-text-2); }
.lp-shots-2 { display:grid; gap:20px; margin-top:20px; }
@media (min-width:860px) { .lp-shots-2 { grid-template-columns:1fr 1fr; } }

.lp-plans { display:grid; gap:14px; }
@media (min-width:680px) { .lp-plans { grid-template-columns:repeat(2,1fr); } }
@media (min-width:1060px) { .lp-plans { grid-template-columns:repeat(4,1fr); } }
.lp-plan { position:relative; background:var(--lp-card); border:1px solid var(--lp-line); border-radius:8px; padding:22px 20px 20px; display:flex; flex-direction:column; transition:border-color .18s ease, transform .18s ease; }
.lp-plan:hover { border-color:var(--lp-brand-edge); }
.lp-plan-hot { background:rgba(34,184,132,.07); border-color:var(--lp-brand-edge); }
.lp-plan-name { font-size:11.5px; font-weight:800; letter-spacing:.11em; text-transform:uppercase; color:var(--lp-text-2); margin-bottom:8px; }
.lp-plan-hot .lp-plan-name { color:var(--lp-brand); }
.lp-price { font-size:29px; font-weight:800; letter-spacing:-.025em; line-height:1.1; font-variant-numeric:tabular-nums; }
.lp-price small { font-size:14px; font-weight:600; color:var(--lp-text-2); letter-spacing:0; }
.lp-plan-cap { font-size:13px; color:var(--lp-text-2); margin:6px 0 0; padding-bottom:16px; border-bottom:1px solid var(--lp-line); }
/* lado a lado, a legenda do Trial quebra em duas linhas e a divisória dele descia
   sozinha: duas linhas de altura para todas deixam as quatro divisórias alinhadas */
@media (min-width:680px) { .lp-plan-cap { min-height:56px; } }
.lp-plan ul { list-style:none; padding:0; margin:16px 0 20px; display:grid; gap:10px; flex:1; align-content:start; }
/* ícone posicionado, não flex: em flex cada trecho vira item separado e a frase parte no meio */
.lp-plan li { position:relative; padding-left:23px; font-size:13.5px; line-height:1.45; color:var(--lp-text-2); }
.lp-plan li.lp-inherit { color:var(--lp-text); font-weight:700; }
.lp-plan li svg { position:absolute; left:0; top:3px; width:15px; height:15px; color:var(--lp-brand); }
.lp-cta-plan { display:block; width:100%; text-align:center; font:inherit; font-size:14px; font-weight:700; padding:11px 14px; border-radius:8px; text-decoration:none; border:1px solid var(--lp-line); background:transparent; color:var(--lp-text); cursor:pointer; transition:background-color .18s ease, border-color .18s ease; }
.lp-cta-plan:hover { border-color:var(--lp-text-3); }
.lp-plan-hot .lp-cta-plan { background:var(--lp-brand); color:#04120C; border-color:var(--lp-brand); }
.lp-plan-hot .lp-cta-plan:hover { background:var(--lp-brand-hover); }
.lp-badge { position:absolute; top:-9px; left:20px; font-size:10.5px; font-weight:800; letter-spacing:.09em; text-transform:uppercase; background:var(--lp-brand); color:#04120C; padding:3px 9px; border-radius:999px; }
.lp-ent { margin-top:14px; border:1px solid var(--lp-line); border-radius:8px; background:var(--lp-card); padding:22px 20px; display:grid; gap:16px; align-items:center; transition:border-color .18s ease; }
.lp-ent:hover { border-color:var(--lp-brand-edge); }
@media (min-width:860px) { .lp-ent { grid-template-columns:auto 1fr auto; gap:28px; } }
.lp-ent-price { font-size:20px; font-weight:800; letter-spacing:-.02em; margin-top:4px; }
.lp-ent p { margin:0; font-size:13.5px; }

.lp-faq { border-bottom:1px solid var(--lp-line); }
.lp-faq summary { list-style:none; cursor:pointer; padding:16px 34px 16px 0; font-size:16.5px; font-weight:700; position:relative; color:var(--lp-text); }
.lp-faq summary::-webkit-details-marker { display:none; }
.lp-faq summary::after { content:"+"; position:absolute; right:4px; top:14px; font-size:21px; font-weight:500; color:var(--lp-brand); }
.lp-faq[open] summary::after { content:"\\2013"; }
.lp-faq p { padding-right:34px; max-width:78ch; margin-top:-4px; }

.lp-header { position:sticky; top:0; z-index:40; background:var(--lp-bg); border-bottom:1px solid var(--lp-line); }
.lp-header-bar { display:flex; align-items:center; gap:14px; padding-block:12px; }
.lp-lock { display:flex; align-items:center; gap:10px; }
.lp-lock-1 { font-size:14.5px; font-weight:700; line-height:1.15; letter-spacing:-.01em; color:var(--lp-text); }
.lp-lock-2 { font-size:12px; font-weight:600; line-height:1.15; color:var(--lp-brand); }
.lp-nav { display:none; gap:22px; margin-inline:auto; }
@media (min-width:1100px) { .lp-nav { display:flex; } }
/* com seis itens, o espanhol quebrava o cabeçalho em 1040px: o menu inteiro só aparece a partir
   de 1100, e até 1200 o espaçamento entre itens encolhe para sobrar respiro */
@media (min-width:1100px) and (max-width:1199px) { .lp-nav { gap:15px; } }
.lp-nav a { color:var(--lp-text-2); text-decoration:none; font-size:14.5px; font-weight:600; transition:color .15s ease; }
.lp-nav a:hover { color:var(--lp-text); }
.lp-actions { display:flex; align-items:center; gap:10px; margin-left:auto; }
@media (min-width:1100px) { .lp-actions { margin-left:0; } }
.lp-signin { font:inherit; font-size:14.5px; font-weight:700; color:var(--lp-text); background:none; border:0; cursor:pointer; display:none; }
@media (min-width:620px) { .lp-signin { display:inline; } }
.lp-burger { display:inline-flex; padding:8px; border-radius:8px; border:1px solid var(--lp-line); background:transparent; color:var(--lp-text-2); cursor:pointer; }
/* a 390px o botao verde espremia o logo em duas linhas e ele proprio em tres.
   Ele e redundante na barra: o mesmo CTA aparece no hero logo abaixo, e o menu
   passa a carrega-lo. */
@media (max-width:619px) { .lp-header .lp-btn-primary { display:none; } }
@media (min-width:1100px) { .lp-burger { display:none; } }
.lp-menu { border-top:1px solid var(--lp-line); padding:14px 20px 18px; display:grid; gap:12px; }
@media (min-width:1100px) { .lp-menu { display:none; } }
.lp-menu a { color:var(--lp-text); text-decoration:none; font-size:15px; font-weight:600; padding-block:6px; }

.lp-footer { background:var(--lp-deep); padding-block:48px; border-top:1px solid var(--lp-line); }
.lp-foot-top { display:flex; flex-wrap:wrap; gap:24px; justify-content:space-between; align-items:flex-start; }
.lp-foot-brand { font-weight:800; margin-bottom:8px; display:flex; align-items:center; gap:9px; color:var(--lp-text); }
.lp-footer a { color:var(--lp-text-2); text-decoration:none; font-size:14px; }
.lp-footer a:hover { color:var(--lp-text); }

@media (prefers-reduced-motion:reduce) { .lp *, .lp *::before, .lp *::after { transition:none !important; animation:none !important; } }

/* ── Demonstração ─────────────────────────────────────────────────────
   Classes de parágrafo levam .lp na frente: .lp p (0,1,1) venceria sozinho. */
.lp-demo { display:grid; grid-template-columns:1fr; gap:32px; align-items:start; }
@media (min-width:900px) { .lp-demo { grid-template-columns:minmax(0,1fr) minmax(0,1.08fr); gap:56px; } }
.lp .lp-demo-lead { margin:16px 0 0; max-width:52ch; }
.lp-demo-points { list-style:none; margin:22px 0 0; padding:0; display:grid; gap:10px; }
.lp-demo-points li { display:flex; gap:10px; align-items:flex-start; font-size:14.5px; color:var(--lp-text-2); }
.lp-demo-points li::before { content:""; flex-shrink:0; width:18px; height:18px; margin-top:1px; border-radius:50%; background:var(--lp-brand-soft) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2322B884' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12.5l4.5 4.5L19 7.5'/%3E%3C/svg%3E") center/11px no-repeat; }
.lp-form { position:relative; background:var(--lp-card); border:1px solid var(--lp-line); border-radius:12px; padding:24px; box-shadow:var(--lp-shadow); }
.lp-form-grid { display:grid; grid-template-columns:1fr; gap:16px; }
@media (min-width:560px) { .lp-form-grid { grid-template-columns:1fr 1fr; } .lp-span { grid-column:1 / -1; } }
.lp-field { display:flex; flex-direction:column; gap:7px; min-width:0; }
.lp-fieldset { border:0; margin:0; padding:0; }
.lp-field > label, .lp-label { font-size:12.5px; font-weight:700; color:var(--lp-text); padding:0; }
.lp-fieldset > legend { margin-bottom:7px; }
.lp-hint { font-weight:500; color:var(--lp-text-2); }
.lp-input { font:inherit; font-size:15px; color:var(--lp-text); background:var(--lp-subtle); border:1px solid var(--lp-line); border-radius:8px; padding:11px 12px; outline:2px solid transparent; outline-offset:2px; width:100%; box-sizing:border-box; transition:border-color .15s ease, box-shadow .15s ease; }
.lp-input::placeholder { color:var(--lp-text-2); }
.lp-input:focus-visible { border-color:var(--lp-brand); box-shadow:0 0 0 3px var(--lp-brand-soft); }
select.lp-input { appearance:none; color-scheme:dark; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238A9990' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 12px center; background-size:16px; padding-right:36px; cursor:pointer; }
select.lp-input:invalid { color:var(--lp-text-2); }
select.lp-input option { color:var(--lp-text); background:var(--lp-card); }
.lp-seg { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:6px; }
.lp-seg-opt { position:relative; display:block; cursor:pointer; }
.lp-seg-opt input { position:absolute; inset:0; opacity:0; margin:0; cursor:pointer; }
.lp-seg-opt span { display:block; text-align:center; font-size:14px; font-weight:700; padding:10px 6px; border-radius:8px; border:1px solid var(--lp-line); background:var(--lp-subtle); color:var(--lp-text-2); transition:border-color .15s ease, background-color .15s ease, color .15s ease; }
.lp-seg-opt:hover span { border-color:var(--lp-text-3); }
/* marcado não pode depender só da cor (WCAG 1.4.1): a borda dobra de espessura */
.lp-seg-opt input:checked + span { background:var(--lp-brand-soft); border-color:var(--lp-brand); color:var(--lp-brand); box-shadow:inset 0 0 0 1px var(--lp-brand); }
.lp-seg-opt input:focus-visible + span { outline:2px solid transparent; outline-offset:2px; border-color:var(--lp-brand); box-shadow:0 0 0 3px var(--lp-brand-soft); }
.lp-seg-opt input:checked:focus-visible + span { box-shadow:inset 0 0 0 1px var(--lp-brand), 0 0 0 3px var(--lp-brand-soft); }
@media (forced-colors: active) { .lp-seg-opt input:checked + span { border:2px solid Highlight; } }
.lp-form .lp-btn-primary { width:100%; justify-content:center; margin-top:20px; }
.lp-form .lp-btn-primary:disabled { opacity:.7; cursor:progress; }
.lp .lp-fine { font-size:12.5px; color:var(--lp-text-2); margin:12px 0 0; text-align:center; }
.lp .lp-form-msg { font-size:13.5px; color:#fca5a5; margin:12px 0 0; text-align:center; }
.lp-form-msg a, .lp-sent a { color:var(--lp-brand); font-weight:700; }
.lp-hp { position:absolute; left:-10000px; top:auto; width:1px; height:1px; overflow:hidden; }
.lp-sent { text-align:center; padding:28px 8px; }
.lp-sent-ico { width:52px; height:52px; margin:0 auto 16px; border-radius:50%; background:var(--lp-brand-soft); color:var(--lp-brand); display:flex; align-items:center; justify-content:center; }
.lp .lp-sent h3 { font-size:20px; outline:none; }
.lp .lp-sent p { max-width:40ch; margin:8px auto 0; }
.lp-sent strong { color:var(--lp-text); overflow-wrap:anywhere; }
`

function Check() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m4 12.5 5 5L20 6.5" />
    </svg>
  )
}

function Arrow() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

export default function Landing() {
  const navigate = useNavigate()
  const { t } = useLang()
  const [menuOpen, setMenuOpen] = useState(false)
  const [demoPreset, setDemoPreset] = useState<{ band: ClientsBand; n: number } | null>(null)

  const goSignup = () => navigate('/onboarding')
  const goLogin = () => navigate('/login')
  // /signup-quick exige plano válido e segue direto para o checkout da Stripe;
  // sem o parâmetro ele devolve o visitante para /plans.
  const goPlan = (plan: string) => navigate(`/signup-quick?plan=${plan}`)
  // O card Enterprise leva ao formulário de demonstração já com a faixa marcada.
  const openDemo = (band: ClientsBand) => {
    setDemoPreset(p => ({ band, n: (p?.n ?? 0) + 1 }))
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    document.getElementById('demo')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  }

  const nav = [
    ['#telas', t('landing.nav.screens')],
    ['#rotina', t('landing.nav.routine')],
    ['#controles', t('landing.nav.controls')],
    ['#precos', t('landing.nav.pricing')],
    ['#faq', t('landing.nav.faq')],
    ['#demo', t('landing.nav.demo')],
  ]

  const steps = [1, 2, 3, 4, 5, 6].map((i) => ({
    n: String(i).padStart(2, '0'),
    title: t(`landing.routine.s${i}t`),
    desc: t(`landing.routine.s${i}d`),
  }))

  const controls = [1, 2, 3].map((i) => ({
    title: t(`landing.controls.c${i}t`),
    desc: t(`landing.controls.c${i}d`),
  }))

  // Preço vem de src/config/plans.ts. Os números de cliente e de acesso nos textos
  // (landing.pricing.*) são conferidos contra o mesmo arquivo em plans-copy.test.ts;
  // quem de fato recusa o registro acima da cota é o trigger do banco.
  const plans = [
    { key: 'trial',   price: `$${PLANS.trial.price}`,   monthly: false, feats: 5, hot: false, onClick: goSignup },
    { key: 'starter', price: `$${PLANS.starter.price}`, monthly: true,  feats: 4, hot: false, onClick: () => goPlan('starter') },
    { key: 'growth',  price: `$${PLANS.growth.price}`,  monthly: true,  feats: 4, hot: true,  onClick: () => goPlan('growth') },
    { key: 'pro',     price: `$${PLANS.pro.price}`,     monthly: true,  feats: 4, hot: false, onClick: () => goPlan('pro') },
  ]

  const faqs = [1, 2, 3, 4].map((i) => ({ q: t(`landing.faq.q${i}`), a: t(`landing.faq.a${i}`) }))

  return (
    <div className="lp">
      <style>{LANDING_CSS}</style>

      {/* ── Cabeçalho ───────────────────────────────────────────────────── */}
      <header className="lp-header">
        <div className="lp-wrap lp-header-bar">
          <div className="lp-lock">
            <ShieldLogo size={30} />
            <div>
              <div className="lp-lock-1">Immigrant Shield</div>
              <div className="lp-lock-2">for Offices</div>
            </div>
          </div>

          <nav className="lp-nav">
            {nav.map(([href, label]) => <a key={href} href={href}>{label}</a>)}
          </nav>

          <div className="lp-actions">
            <LangThemePicker hideTheme />
            <button className="lp-signin" onClick={goLogin}>{t('landing.signIn')}</button>
            <button className="lp-btn lp-btn-primary" style={{ padding: '9px 16px', fontSize: 14 }} onClick={goSignup}>
              {t('landing.startFree')}
            </button>
            <button
              className="lp-burger"
              aria-label={menuOpen ? t('landing.menuClose') : t('landing.menuOpen')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lp-menu">
            {nav.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>
            ))}
            <button className="lp-btn lp-btn-primary" onClick={goSignup}>{t('landing.startFree')}</button>
            <button className="lp-cta-plan" onClick={goLogin}>{t('landing.signIn')}</button>
          </div>
        )}
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-media">
          {/* é o LCP da página: o index.html faz preload deste mesmo arquivo */}
          <img src="/landing-hero.webp" width={1584} height={672} alt={t('landing.hero.photoAlt')} fetchPriority="high" />
        </div>
        <div className="lp-hero-copy">
          <div className="lp-wrap">
            <div className="lp-col">
              <p className="lp-eyebrow">{t('landing.hero.eyebrow')}</p>
              <h1>{t('landing.hero.title')}</h1>
              <p className="lp-lead">{t('landing.hero.body')}</p>
              <div className="lp-btn-row">
                <button className="lp-btn lp-btn-primary" onClick={goSignup}>
                  {t('landing.startFree')}<Arrow />
                </button>
                <a className="lp-btn lp-btn-ghost" href="#demo">{t('landing.hero.ctaDemo')}</a>
              </div>
              <p style={{ fontSize: 13.5, marginBottom: 20 }}>{t('landing.hero.freeNote')}</p>
              <p className="lp-scope">{t('landing.hero.scope')}</p>
            </div>
          </div>
          <p className="lp-photo-cap">{t('landing.hero.photoCaption')}</p>
        </div>
      </section>

      {/* ── As telas ────────────────────────────────────────────────────── */}
      <section className="lp-band lp-canvas" id="telas">
        <div className="lp-wrap">
          <div className="lp-head">
            <p className="lp-eyebrow">{t('landing.screens.eyebrow')}</p>
            <div className="lp-head-cols">
              <h2>{t('landing.screens.title')}</h2>
              <div>
                <p>{t('landing.screens.body')}</p>
                <div className="lp-chips">
                  <span className="lp-chip lp-chip-crit">{t('landing.screens.chipCritical')}</span>
                  <span className="lp-chip lp-chip-warn">{t('landing.screens.chipWarning')}</span>
                  <span className="lp-chip lp-chip-info">{t('landing.screens.chipInfo')}</span>
                </div>
              </div>
            </div>
          </div>

          {SHOTS_READY && (
            <>
              <div>
                <figure className="lp-shot">
                  <div className="lp-chrome"><i /><i /><i /><span>{SHOTS[0].chrome}</span></div>
                  <img
                    src={SHOTS[0].file}
                    srcSet={`/landing-overview-720.webp 720w, /landing-overview-1440.webp 1440w, ${SHOTS[0].file} 2880w`}
                    sizes="(min-width:1152px) 1112px, calc(100vw - 40px)"
                    alt={t(`landing.screens.${SHOTS[0].altKey}`)} width={2880} height={1800} loading="lazy" decoding="async"
                  />
                  <figcaption>{t(`landing.screens.${SHOTS[0].capKey}`)}</figcaption>
                </figure>
              </div>
              <div className="lp-shots-2">
                {SHOTS.slice(1).map((s) => (
                  <figure className="lp-shot" key={s.file}>
                    <div className="lp-chrome"><i /><i /><i /><span>{s.chrome}</span></div>
                    <img
                      src={s.file}
                      srcSet={`${s.file.replace('.webp', '-640.webp')} 640w, ${s.file} 1096w`}
                      sizes="(min-width:1152px) 546px, (min-width:860px) calc(50vw - 30px), calc(100vw - 40px)"
                      alt={t(`landing.screens.${s.altKey}`)} width={1096} height={685} loading="lazy" decoding="async"
                    />
                    <figcaption>{t(`landing.screens.${s.capKey}`)}</figcaption>
                  </figure>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── A rotina ────────────────────────────────────────────────────── */}
      <section className="lp-band lp-lift" id="rotina">
        <div className="lp-wrap">
          <div className="lp-head">
            <p className="lp-eyebrow">{t('landing.routine.eyebrow')}</p>
            <div className="lp-head-cols">
              <h2>{t('landing.routine.title')}</h2>
              <div><p>{t('landing.routine.body')}</p></div>
            </div>
          </div>
          <div className="lp-grid lp-g3">
            {steps.map((s) => (
              <div className="lp-cell lp-step" key={s.n}>
                <span className="lp-num" aria-hidden="true">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Controles ───────────────────────────────────────────────────── */}
      <section className="lp-band lp-deep" id="controles">
        <div className="lp-wrap">
          <div className="lp-head">
            <p className="lp-eyebrow">{t('landing.controls.eyebrow')}</p>
            <div className="lp-head-cols">
              <h2>{t('landing.controls.title')}</h2>
              <div><p>{t('landing.controls.body')}</p></div>
            </div>
          </div>
          <div className="lp-grid lp-g3">
            {controls.map((c) => (
              <div className="lp-cell" key={c.title}>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Planos ──────────────────────────────────────────────────────── */}
      <section className="lp-band lp-canvas" id="precos">
        <div className="lp-wrap">
          <div className="lp-head">
            <p className="lp-eyebrow">{t('landing.pricing.eyebrow')}</p>
            <div className="lp-head-cols">
              <h2>{t('landing.pricing.title')}</h2>
              <div><p>{t('landing.pricing.body')}</p></div>
            </div>
          </div>

          <div className="lp-plans">
            {plans.map((p) => (
              <div className={`lp-plan${p.hot ? ' lp-plan-hot' : ''}`} key={p.key}>
                {p.hot && <span className="lp-badge">{t('landing.pricing.badge')}</span>}
                <div className="lp-plan-name">{t(`landing.pricing.${p.key}Name`)}</div>
                <div className="lp-price">
                  {p.price}{p.monthly && <small>{' '}{t('landing.pricing.perMonth')}</small>}
                </div>
                <p className="lp-plan-cap">{t(`landing.pricing.${p.key}Cap`)}</p>
                <ul>
                  {Array.from({ length: p.feats }, (_, i) => {
                    // O primeiro item dos planos pagos é sempre o "tudo do plano
                    // anterior", e é o que carrega o destaque.
                    const inherit = p.key !== 'trial' && i === 0
                    return (
                      <li key={i} className={inherit ? 'lp-inherit' : undefined}>
                        <Check />{t(`landing.pricing.${p.key}F${i + 1}`)}
                      </li>
                    )
                  })}
                </ul>
                <button className="lp-cta-plan" onClick={p.onClick}>{t(`landing.pricing.${p.key}Cta`)}</button>
              </div>
            ))}
          </div>

          <div className="lp-ent">
            <div>
              <div className="lp-plan-name">{t('landing.pricing.entName')}</div>
              <div className="lp-ent-price">{t('landing.pricing.entPrice')}</div>
            </div>
            <p>{t('landing.pricing.entBody')}</p>
            <button className="lp-cta-plan" style={{ whiteSpace: 'nowrap' }} onClick={() => openDemo('over500')}>
              {t('landing.pricing.entCta')}
            </button>
          </div>

        </div>
      </section>

      {/* ── Perguntas ───────────────────────────────────────────────────── */}
      <section className="lp-band lp-lift" id="faq">
        <div className="lp-wrap" style={{ maxWidth: 860 }}>
          <div className="lp-head" style={{ marginBottom: 20 }}>
            <p className="lp-eyebrow">{t('landing.faq.eyebrow')}</p>
            <h2>{t('landing.faq.title')}</h2>
          </div>
          {faqs.map((f, i) => (
            <details className="lp-faq" key={f.q} open={i === 0}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Demonstração ────────────────────────────────────────────────── */}
      <section className="lp-band lp-canvas" id="demo">
        <div className="lp-wrap">
          <DemoRequest preset={demoPreset} />
        </div>
      </section>

      {/* ── Começar ─────────────────────────────────────────────────────── */}
      <section className="lp-band lp-deep">
        <div className="lp-wrap" style={{ maxWidth: 720, textAlign: 'center' }}>
          <p className="lp-eyebrow">{t('landing.cta.eyebrow')}</p>
          <h2 style={{ marginBottom: 14 }}>{t('landing.cta.title')}</h2>
          <p style={{ marginInline: 'auto', maxWidth: '60ch' }}>{t('landing.cta.body')}</p>
          <div className="lp-btn-row" style={{ justifyContent: 'center', marginTop: 22, marginBottom: 0 }}>
            <button className="lp-btn lp-btn-primary" onClick={goSignup}>
              {t('landing.startFree')}<Arrow />
            </button>
            <button className="lp-btn lp-btn-ghost" onClick={goLogin}>{t('landing.cta.secondary')}</button>
          </div>
        </div>
      </section>

      {/* ── Rodapé ──────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-wrap">
          <div className="lp-foot-top">
            <div style={{ maxWidth: '46ch' }}>
              <div className="lp-foot-brand">
                <ShieldLogo size={26} />
                <span>Immigrant Shield <span style={{ color: 'var(--lp-brand)' }}>for Offices</span></span>
              </div>
              <p style={{ fontSize: 14, margin: 0 }}>{t('landing.footer.tagline')}</p>
            </div>
            <div style={{ display: 'flex', gap: 22 }}>
              <Link to="/legal">{t('landing.footer.legal')}</Link>
              <Link to="/faq">{t('landing.footer.faq')}</Link>
              <Link to="/support">{t('landing.footer.support')}</Link>
              <Link to="/about">{t('landing.footer.about')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
